/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { ActivityIndicator, StyleSheet, Switch, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ShellScrollScreen } from '../../shared/layout/ShellScrollScreen';
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { settingsStyles } from './settingsScreen.styles';
import { useBusinessFunctionsScreen } from './useBusinessFunctionsScreen';
import { BUSINESS_FUNCTIONS_HUB_ROW } from '../domain/businessFunctionsCatalog';

export const BusinessFunctionsScreen = () => {
  const vm = useBusinessFunctionsScreen();
  const { layoutWebShell } = useContentLayout('settings');
  const widthVariant = layoutWebShell ? 'settingsWide' : 'settings';

  if (vm.loading) {
    return (
      <ShellScrollScreen widthVariant={widthVariant}>
        <View style={local.loadingRow}>
          <ActivityIndicator color={ticketColors.gold} />
          <Text style={settingsStyles.caption}>Cargando funciones…</Text>
        </View>
      </ShellScrollScreen>
    );
  }

  return (
    <ShellScrollScreen widthVariant={widthVariant}>
      <Text style={settingsStyles.subtitle}>{BUSINESS_FUNCTIONS_HUB_ROW.description}</Text>
      {!vm.isOwner ? (
        <Text style={settingsStyles.caption}>Solo el dueño puede cambiar esto.</Text>
      ) : null}

      {vm.groupedRows.map((group) => (
        <View key={group.id} style={local.group}>
          <Text style={local.groupTitle}>{group.label}</Text>
          <View style={settingsStyles.card}>
            {group.rows.map((row, index) => (
              <View key={row.code}>
                {index > 0 ? <View style={local.separator} /> : null}
                <View style={local.row}>
                  <View style={local.iconWrap}>
                    <Ionicons name={row.iconName} size={16} color={ticketColors.ink2} />
                  </View>
                  <View style={local.body}>
                    <Text style={settingsStyles.navRowTitle}>{row.label}</Text>
                    <Text style={settingsStyles.navRowDescription}>{row.explanation}</Text>
                    {row.alwaysOn ? (
                      <Text style={local.chip}>Siempre activo</Text>
                    ) : row.addedByOwner ? (
                      <Text style={local.chip}>Agregada por vos</Text>
                    ) : row.inRubro ? (
                      <Text style={local.chipMuted}>Del rubro</Text>
                    ) : null}
                    {row.planLocked ? (
                      <TouchableOpacity
                        onPress={vm.openBilling}
                        accessibilityRole="button"
                        accessibilityLabel="Ver planes para habilitar esto"
                      >
                        <Text style={local.upgrade}>Tu plan no incluye esto. Ver planes</Text>
                      </TouchableOpacity>
                    ) : null}
                  </View>
                  {row.alwaysOn ? (
                    <Ionicons name="lock-closed-outline" size={18} color={ticketColors.ink2} />
                  ) : vm.savingCode === row.code ? (
                    <ActivityIndicator color={ticketColors.gold} />
                  ) : (
                    <Switch
                      value={row.enabled}
                      onValueChange={(next) => vm.requestToggle(row, next)}
                      disabled={!vm.isOwner || row.planLocked || vm.savingCode != null}
                      trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
                      thumbColor={ticketColors.ink}
                      accessibilityRole="switch"
                      accessibilityLabel={row.label}
                      testID={`config.business-functions.${row.code}-switch`}
                    />
                  )}
                </View>
              </View>
            ))}
          </View>
        </View>
      ))}

      <OperationalConfirmModal
        visible={vm.pendingOff != null}
        title={vm.pendingOff?.label ?? 'Desactivar'}
        message={vm.pendingOff?.hideConfirm ?? ''}
        confirmLabel="Desactivar"
        destructive
        iconName={vm.pendingOff?.iconName ?? 'eye-off-outline'}
        onClose={vm.closeConfirm}
        onConfirm={() => void vm.confirmHide()}
      />
    </ShellScrollScreen>
  );
};

const local = StyleSheet.create({
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
  },
  group: {
    gap: spacing.xs,
    marginBottom: spacing.md,
  },
  groupTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
  },
  iconWrap: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  body: {
    flex: 1,
    minWidth: 0,
    gap: 2,
    paddingRight: spacing.sm,
  },
  separator: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: ticketColors.ruleSoft,
  },
  chip: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.gold,
  },
  chipMuted: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  upgrade: {
    marginTop: 4,
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.gold,
  },
});
